import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import { CertificateSettingType, getCertificationSettingDefaultAttendance, getCertificationSettingDefaultAverageScore, getCertificationSettingDefaultExam, getCertificationSettingDefaultProgress, ICertificateSettingItemConfiguration, ICertificateSettingsItem, ICertificateSettingsModule } from 'app_types/admin/certificateSettings/certificateSettings.types'
import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { IoIosAddCircleOutline } from 'react-icons/io'
import { ICourseSchedulingDetailListItem } from 'app_types/admin/courseScheduling/courseSchedulingDetailsTypes';

import './CertificateSettingsModuleForm.scss'
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent';
import ReactSelectComponent from 'app_components/commons/reactSelect';
import { useForm } from 'react-hook-form';
import { MdDelete } from 'react-icons/md';
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent';
import CertificateSettingsItemForm from '../CertificateSettingsItemForm/CertificateSettingsItemForm';
import InputComponent from 'app_components/commons/Input/InputComponent';
import DurationFieldService from 'app_services/components/durationFieldService';
import CourseSchedulingDetailsService from 'app_services/admin/courseScheduling/courseSchedulingDetailsService';
import LoadingWrapper from 'app_components/commons/LoadingWrapper/LoadingWrapper';

interface ICertificateSettingsModuleFormProps {
  courseSchedulingDetails: ICourseSchedulingDetailListItem[]
  onChange: (data: ICertificateSettingsModule[]) => void
  defaultValue?: ICertificateSettingsModule[]
  config: ICertificateSettingItemConfiguration
  programMoodleId: string
  certificationType: CertificateSettingType
}

enum CertificateSettingsItems {
  EXAM = 'exam',
  ATTENDANCE = 'attendance',
  PROGRESS = 'progress',
  AVERAGE_SCORE = 'averageScore',
}

interface IModuleExam {
  id: string
  hasExam: boolean
}

const CertificateSettingsModuleForm: FC<ICertificateSettingsModuleFormProps> = ({
  courseSchedulingDetails,
  onChange,
  defaultValue,
  config,
  programMoodleId,
  certificationType,
}) => {
  const placeholderForm = useForm()
  const [modules, setModules] = useState<ICertificateSettingsModule[]>(defaultValue ? defaultValue : [])
  const [moduleExams, setModuleExams] = useState<IModuleExam[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const courseSchedulingDetailsService = useMemo(() => new CourseSchedulingDetailsService(), [])
  const listOfCourses = useMemo(() =>
  // @ts-ignore
    courseSchedulingDetails?.filter((course) => !modules?.some((module) => module.courseSchedulingDetail?.value === course?._id))
    ?.map((course) => ({ label: course?.course?.name, value: course?._id })),
    [courseSchedulingDetails, modules],
  )
  const durationFieldService = useMemo(() => new DurationFieldService(), [])

  const getDurationFromModule = useCallback((courseId: string) => {
    return courseSchedulingDetails?.find((course) => course?._id === courseId)?.duration
  }, [courseSchedulingDetails])

  const getModuleExams = useCallback(async () => {
    try {
      setIsLoading(true)
      if (courseSchedulingDetails?.length && certificationType === CertificateSettingType.ATTENDANCE_APPROVAL) {
        const sectionMoodleIds = courseSchedulingDetails.map(course => course?.course?.moodle_id).filter(Boolean)
        const response = await courseSchedulingDetailsService.hasExam(programMoodleId, sectionMoodleIds)

        if (response?.status === "success" && response?.sections?.length) {
          const newModuleExams: IModuleExam[] = courseSchedulingDetails
            .filter(course => course?.course?.moodle_id)
            .map(course => {
              const sectionResult = response.sections.find(
                section => section.sectionId === course.course.moodle_id
              );
              return {
                id: course._id,
                hasExam: sectionResult?.hasExam || false
              };
            })
            .filter(module => module.hasExam)
            setModuleExams(newModuleExams)
        }
      } else {
        setModuleExams([])
      }
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      console.error('Module exams ERROR: ', e)
    }
  }, [
    courseSchedulingDetails,
    courseSchedulingDetailsService,
    programMoodleId,
    certificationType
  ])

  useEffect(() => {
    getModuleExams()
  }, [getModuleExams])

  useEffect(() => {
    onChange && onChange(modules)
  }, [JSON.stringify(modules)])

  useEffect(() => {
    const newItems = [...modules]
    if (newItems?.length) {
      setModules(newItems.map((module) => {
        if (!config?.attendance) delete module.attendance
        if (!config?.exam) delete module.exam
        if (!config?.progress) delete module.progress
        if (!config?.averageScore) delete module.averageScore

        if (config?.attendance && !module.attendance) module.attendance = getCertificationSettingDefaultAttendance()
        if (config?.exam && !module.exam) module.exam = getCertificationSettingDefaultExam()
        if (config?.progress && !module.progress) module.progress = getCertificationSettingDefaultProgress()
        if (config?.averageScore && !module.averageScore) module.averageScore = getCertificationSettingDefaultAverageScore()

        return module
      }))
    }
  }, [config])

  const handleChangeCourse = useCallback((newCourse: any, idx: number) => {
    const newValue = [...modules]
    const duration = getDurationFromModule(newCourse?.value)
    newValue[idx].courseSchedulingDetail = newCourse
    newValue[idx].duration = duration ? duration : 0
    newValue[idx].durationValidation = 0
    setModules(newValue)
  }, [modules, getDurationFromModule])

  const handleAddModule = useCallback(() => {
    const defaultCourse = listOfCourses[0]
    const defaultDuration = getDurationFromModule(defaultCourse?.value)
    const newItem: ICertificateSettingsModule = {
      // @ts-ignore
      courseSchedulingDetail: defaultCourse,
      duration: defaultDuration ? defaultDuration : 0,
      durationValidation: 0,
    }

    if (config?.attendance) newItem.attendance = getCertificationSettingDefaultAttendance()
    if (config?.exam) newItem.exam = getCertificationSettingDefaultExam()
    if (config?.progress) newItem.progress = getCertificationSettingDefaultProgress()
    if (config?.averageScore) newItem.averageScore = getCertificationSettingDefaultAverageScore()

    setModules([...modules, newItem])
  }, [listOfCourses, modules, getDurationFromModule, config])

  const handleClickDeleteModule = (idx: number) => {
    const newItems = [...modules]
    newItems.splice(idx, 1)
    setModules(newItems)
  }

  const handleChangeItem = (idx: number, key: CertificateSettingsItems, value: ICertificateSettingsItem) => {
    const newItems = [...modules]
    newItems[idx][key] = value
    setModules(newItems)
  }

  const handleChangeDuration = (e: ChangeEvent<HTMLInputElement>, idx: number) => {
    const newItems = [...modules]
    // @ts-ignore
    const defaultDuration = getDurationFromModule(newItems[idx].courseSchedulingDetail?.value)
    const duration = durationFieldService.getSecondsFromDuration(e.target.value)
    newItems[idx].duration = duration
    if (!!defaultDuration && !!duration && defaultDuration !== duration) {
      newItems[idx].durationValidation = duration - defaultDuration
    } else {
      newItems[idx].durationValidation = 0
    }
    setModules(newItems)
  }

  return (
    <div className="certificate-settings-module-form--layout">
      <ButtonComponent
        onClick={handleAddModule}
        variant='primary'
        textTransform='initial'
        type='button'
        width='fit-content'
        disabled={!listOfCourses?.length || isLoading}
      >
        <ButtonComponent.Icon>
          <IoIosAddCircleOutline />
        </ButtonComponent.Icon>
        Agregar modulo
      </ButtonComponent>
      <LoadingWrapper loading={isLoading}>
        <div className='certificate-settings-module-form--items'>
          {modules?.map((module, idx) => (
            // @ts-ignore
            <div key={module.courseSchedulingDetail?.value} className='certificate-settings-module-form--item'>
              <div
                onClick={() => handleClickDeleteModule(idx)}
                className='certificate-settings-module-form--item-delete'
              >
                <TooltipComponent text='Eliminar modulo'>
                  <MdDelete />
                </TooltipComponent>
              </div>
              <div>
                <LabelComponent>
                  Modulo
                </LabelComponent>
                <ReactSelectComponent
                  identifier={`course-${idx}`}
                  formHooks={placeholderForm}
                  options={listOfCourses}
                  isMulti={false}
                  defaultValue={[module?.courseSchedulingDetail]}
                  onChangeSelect={(e: any) => handleChangeCourse(e, idx)}
                  disableClear
                />
              </div>
              <InputComponent
                label='Duración'
                placeholder='00h 00m 00s'
                defaultValue={durationFieldService.getDurationFormated(module?.duration)}
                onChange={(e) => handleChangeDuration(e, idx)}
                warningText={!!module?.durationValidation ? "La duración configurada es diferente a la programada" : undefined}
              />
              <div className='certificate-settings-module-form--setting-items'>
                {/* @ts-ignore */}
                {!!module?.exam && moduleExams?.some((exam) => exam.id === module?.courseSchedulingDetail?.value) && (
                  <div>
                    <LabelComponent>
                      Examen
                    </LabelComponent>
                    <CertificateSettingsItemForm
                      onChange={(newValue) => handleChangeItem(idx, CertificateSettingsItems.EXAM, newValue)}
                      value={module?.exam}
                    />
                  </div>
                )}
                {!!module?.attendance && (
                  <div>
                    <LabelComponent>
                      Asistencia
                    </LabelComponent>
                    <CertificateSettingsItemForm
                      onChange={(newValue) => handleChangeItem(idx, CertificateSettingsItems.ATTENDANCE, newValue)}
                      value={module?.attendance}
                    />
                  </div>
                )}
                {!!module?.progress && (
                  <div>
                    <LabelComponent>
                      Avance
                    </LabelComponent>
                    <CertificateSettingsItemForm
                      onChange={(newValue) => handleChangeItem(idx, CertificateSettingsItems.PROGRESS, newValue)}
                      value={module?.progress}
                    />
                  </div>
                )}
                {!!module?.averageScore && (
                  <div>
                    <LabelComponent>
                      Nota promedio
                    </LabelComponent>
                    <CertificateSettingsItemForm
                      onChange={(newValue) => handleChangeItem(idx, CertificateSettingsItems.AVERAGE_SCORE, newValue)}
                      value={module?.averageScore}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </LoadingWrapper>
    </div>
  )
}

export default CertificateSettingsModuleForm
